import { get, compact } from "lodash"
import { createSelector } from "reselect"
import { Creators as ResourceCreators } from "modules/resources/model"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { Creators as DialogCreators } from "modules/dialog/model"
import { PERMISSION_LEVELS, hasPermission } from "helpers/permissions"
import { upsellingTitle } from "helpers/op-pro"
import { getCurrentWatchlist } from "."
import { getFavorites } from "selectors/favorites"

import Confirm from "components/confirm-dialog"
import EditWatchlist from "components/edit-watchlist"
import saveRecentResources from "hocs/saveRecentResources"

const getAccount = state => state.global.selected.account
const getPermissionLevel = state => get(state, "global.data.resourcePermission.data.resource.permissionLevel")

const EMPTY_ITEMS = []

// All of these now run while the watchlist is still in flight.
const getIsFavorite = createSelector(
  [getCurrentWatchlist, getFavorites],
  (watchlist, favorites) => (watchlist ? get(favorites, watchlist.id, false) : false),
)

const getWatchlistWithMeta = createSelector(
  [getCurrentWatchlist, getIsFavorite, getPermissionLevel],
  (watchlist, isFavorite, permissionLevel) => ({
    ...watchlist,
    isFavorite,
    permissionLevel,
  }),
)

export const getContextualMenuItems = createSelector(
  [getAccount, getWatchlistWithMeta, getCurrentWatchlist],
  (account, watchlistWithMeta, watchlist) => (watchlist ? getWatchlistMenuItems(account, watchlistWithMeta) : EMPTY_ITEMS),
)

export function getWatchlistMenuItems(account, watchlist, isOpPro) {
  const { toggleFavorite, updateWatchlist, deleteWatchlist } = ResourceCreators
  const { open: openDialog, close: closeDialog } = DialogCreators
  const { add: addNotification } = NotificationCreators
  const { id, alias, name, isFavorite, permissionLevel } = watchlist

  const editable = hasPermission(permissionLevel, PERMISSION_LEVELS.WRITE)
  const deletable = hasPermission(permissionLevel, PERMISSION_LEVELS.OWNER)

  const EditWatchlistWithRecentResources = saveRecentResources(EditWatchlist, "id")

  return compact([
    editable && {
      id: "edit",
      label: "Edit",
      action: (dispatch) => dispatch(openDialog({
        title: "Edit watchlist",
        // [TODO] [#184018795] JSX shouldn't be in selectors or models
        content: <EditWatchlistWithRecentResources {...watchlist} saveWatchlist={(...args) => dispatch(updateWatchlist(...args))} />,
      })),
    },
    {
      id: "toggleFavorite",
      label: isFavorite ? "Remove from favorites" : "Add to favorites",
      action: (dispatch) => {
        dispatch(toggleFavorite(id, "watchlist", !isFavorite))
        dispatch(addNotification("info", null, `${name} successfully ${isFavorite ? "removed from" : "added to"} favorites`))
      },
    },
    editable && {
      id: "upload",
      label: "Upload positions",
      to: `/${account}/watchlist/${alias}/upload`,
    },
    {
      id: "permissions",
      label: "View permissions",
      to: `/${account}/watchlist/${alias}/permissions/members`,
      disabledText: isOpPro && upsellingTitle(isOpPro, "watchlist"),
    },
    deletable && {
      id: "delete",
      label: "Delete",
      className: "danger",
      action: (dispatch, navigate) => {
        dispatch(openDialog({
          title: `Delete watchlist: ${name}`,
          content: <Confirm
            text={`Are you sure you want to delete ${name}? This will remove securities from this watchlist and permanently delete it.`}
            keyword={name}
            cta="Delete watchlist"
            action={() => {
              dispatch(deleteWatchlist(id))
              navigate(`/${account}/home/resources`)
            }}
            onClose={() => dispatch(closeDialog())}
          />,
        }))
      },
    },
  ])
}
